import { useTranslation } from "react-i18next";
import {
  FormEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Checkbox,
  Form,
  FormContext,
  FormErrorTypes,
  Modal,
  ModalContext,
} from "dash-ui";

import {
  GetPropertyByIdQuery,
  GetUnitsDocument,
  PropertyTransactionTypesEnum,
  useCreateUnitMutation,
  useEditUnitMutation,
} from "gql/generated.graphql";
import useValidation from "./hooks/useValidation";
import NameRow from "./components/NameRow/NameRow";
import BedroomBathroomRow from "./components/BedroomBathroomRow/BedroomBathroomRow";
import PriceRow from "./components/PriceRow/PriceRow";
import FloorPlan from "./components/FloorPlan/FloorPlan";
import { FormFields } from "./constants/constants";
import TypeRow from "./components/TypeRow/TypeRow";
import InteriorSqftRow from "./components/InteriorSqftRow/InteriorSqftRow";
import ExteriorSqftRow from "./components/ExteriorSqftRow/ExteriorSqftRow";
import { InventoryTypeEnum, unitSum } from "dash-common";
import { isNil } from "lodash";

const handleSqftSum = (
  interiorMax?: number | string | null,
  exteriorMax?: number | string | null
): number => {
  if (isNil(interiorMax) && isNil(exteriorMax)) return 0;
  return unitSum(interiorMax, exteriorMax);
};

const AddPropertyInventoryModal = ({
  property,
}: {
  property: GetPropertyByIdQuery["getPropertyById"];
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation(["forms", "common", "pages"]);
  const {
    formState,
    formOnSubmit,
    formOnError,
    resetForm,
    resetFormErrors,
    setFormValues,
  } = useContext(FormContext);
  const {
    isOpen,
    toggleModal,
    setPayload,
    payload: inventory,
  } = useContext(ModalContext);
  const valid = useValidation();
  const isEditMode = Boolean(inventory);

  const [createUnit, { loading: createLoading }] = useCreateUnitMutation({
    refetchQueries: [GetUnitsDocument],
  });
  const [editUnit, { loading: editLoading }] = useEditUnitMutation({
    refetchQueries: [GetUnitsDocument],
  });

  const [noChangelog, setNoChangelog] = useState(false);

  useEffect(() => {
    setFormValues({
      averageBathroomsNumber: {},
      averageBedroomsNumber: {},
      averageInteriorSqft: {},
      averageExteriorSqft: {},
      averageTotalSqft: {},
      inventoryType: InventoryTypeEnum.MODEL,
    });
  }, [isOpen]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    if (inventory) {
      setFormValues({
        ...inventory,
        prices: {
          min: inventory.startingPrice,
          max: inventory.highestPrice,
        },
        averageBathroomsNumber: {
          min: inventory.averageBathroomsNumber?.min,
          max: inventory.averageBathroomsNumber?.max
            ? +inventory.averageBathroomsNumber?.max
            : null,
        },
        averageBedroomsNumber: {
          min: inventory.averageBedroomsNumber?.min,
          max: +inventory.averageBedroomsNumber?.max
            ? +inventory.averageBedroomsNumber?.max
            : null,
        },
        floorPlanFile: inventory.floorPlanFile,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory]);

  const closeModal = () => {
    resetForm();
    resetFormErrors();
    toggleModal(false);
    setPayload(undefined);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const { isLoading, errorSlug, ...formValues } = formState;

    if (
      !valid(FormFields.Name, formValues.name) ||
      !valid(FormFields.Prices, formValues.prices) ||
      !valid(FormFields.Bedrooms, formValues.averageBedroomsNumber) ||
      !valid(FormFields.Bathrooms, formValues.averageBathroomsNumber) ||
      !valid(FormFields.InteriorSqft, formValues.averageInteriorSqft) ||
      !valid(FormFields.ExteriorSqft, formValues.averageExteriorSqft)
    )
      return;

    const {
      inventoryType,
      name,
      averageBedroomsNumber,
      averageBathroomsNumber,
      averageInteriorSqft,
      averageExteriorSqft,
      prices,
      floorPlanFileId,
      customRooms,
    } = formValues;

    const minAvgInterior = +averageInteriorSqft?.min;
    const maxAvgInterior =
      averageInteriorSqft?.max || averageInteriorSqft?.max === 0
        ? +averageInteriorSqft?.max
        : null;
    const minAvgExterior = +averageExteriorSqft?.min;
    const maxAvgExterior =
      averageExteriorSqft?.max || averageExteriorSqft?.max === 0
        ? +averageExteriorSqft?.max
        : null;

    const input = {
      inventoryType,
      name,
      averageTotalSqft: {
        min: handleSqftSum(minAvgInterior, minAvgExterior),
        max: handleSqftSum(maxAvgInterior, maxAvgExterior),
      },
      averageInteriorSqft: {
        min: minAvgInterior,
        max: maxAvgInterior,
      },
      averageExteriorSqft: {
        min: minAvgExterior,
        max: maxAvgExterior,
      },
      startingPrice: prices?.min,
      highestPrice: prices?.max,
      customRooms,
      averageBedroomsNumber: {
        min: +averageBedroomsNumber?.min,
        max:
          averageBedroomsNumber?.max || averageBedroomsNumber?.max === 0
            ? +averageBedroomsNumber?.max
            : null,
      },
      averageBathroomsNumber: {
        min: +averageBathroomsNumber?.min,
        max:
          averageBathroomsNumber?.max || averageBathroomsNumber?.max === 0
            ? +averageBathroomsNumber?.max
            : null,
      },
      floorPlanFileId,
    };

    const onError = (error: { message: string }) => {
      formOnError(t(`common:serverMessages.${error.message}`) || error.message);
    };

    if (isEditMode) {
      return editUnit({
        variables: {
          input: { unitDetails: input, unitId: inventory?._id, noChangelog },
        },
        onCompleted: closeModal,
        onError,
      });
    }
    return createUnit({
      variables: { input: { ...input, propertyId: property._id } },
      onCompleted: closeModal,
      onError,
    });
  };

  const isLoading =
    createLoading || editLoading || formState[FormFields.FloorPlanLoading];

  const renderChangelogCheckbox = () => {
    return (
      <div className="my-4">
        <Checkbox
          label={t("pages:propertyPage.addToChangelogCheckbox") || ""}
          checked={!noChangelog}
          onChange={() => setNoChangelog(!noChangelog)}
        />
      </div>
    );
  };

  const isRent = useMemo(
    () => property.transactionType === PropertyTransactionTypesEnum.ForRent,
    [property]
  );

  return (
    <Modal
      lg
      title={
        isEditMode
          ? t("pages:propertyPage.inventoryEditModalTitle") || ""
          : t("pages:propertyPage.inventoryCreateModalTitle") || ""
      }
      isOpen={isOpen}
      onRequestClose={closeModal}
      submitButton={{
        title: isEditMode ? t("common:labels.update") : t("common:labels.add"),
        isLoading,
        disabled: isLoading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t("common:labels.cancel"),
        onClick: closeModal,
      }}
    >
      <Form ref={formRef} onSubmit={onSubmit}>
        <TypeRow />
        <NameRow />
        <BedroomBathroomRow />
        <InteriorSqftRow />
        <ExteriorSqftRow />
        <PriceRow isRent={isRent} />
        <FloorPlan />
        <Form.Group mt={3}>
          <Form.Feedback $errorType={FormErrorTypes.Invalid}>
            {formState.errorSlug}
          </Form.Feedback>
        </Form.Group>
        {renderChangelogCheckbox()}
      </Form>
    </Modal>
  );
};

export default AddPropertyInventoryModal;
